// Pure audio helpers used by DeepgramStreamingEngine. Extracted so the
// resample/Int16 hot path can be unit-tested without spinning up an
// AudioContext, and so the engine class stays under file-size hygiene.
// Phase 3 adds parameterized target rate; pcmResample.worklet.js mirrors
// this logic via public/asr/deepgramPcmCore.js.
export const TARGET_SAMPLE_RATE_16K = 16_000;
export const TARGET_SAMPLE_RATE_8K = 8_000;

/** Linear resample mono Float32 PCM to `targetRate` Hz. */
export function resampleToTargetRate(
  input: Float32Array,
  inRate: number,
  targetRate: number,
): Float32Array {
  if (inRate === targetRate) return input;
  if (!input.length) return input;
  const outLen = Math.round((input.length * targetRate) / inRate);
  const output = new Float32Array(outLen);
  const ratio = inRate / targetRate;
  for (let i = 0; i < outLen; i++) {
    const srcPos = i * ratio;
    const i0 = Math.floor(srcPos);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const frac = srcPos - i0;
    output[i] = input[i0] * (1 - frac) + input[i1] * frac;
  }
  return output;
}

export function resampleTo16k(input: Float32Array, inRate: number): Float32Array {
  return resampleToTargetRate(input, inRate, TARGET_SAMPLE_RATE_16K);
}

export function float32ToInt16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

/** ~1 MB ≈ 30s of 16 kHz mono Int16 PCM — Phase 2 backpressure cap. */
export const PCM_BUFFERED_AMOUNT_CAP = 1_000_000;

export type ProcessPcmFrameResult =
  | { status: "sent"; bytesSent: number }
  | { status: "dropped"; reason: "backpressure" }
  | { status: "skipped"; reason: "closed" | "empty" | "error" };

type PcmWebSocket = Pick<WebSocket, "readyState" | "send" | "bufferedAmount">;

/**
 * Resample → Int16 PCM → ws.send. Drops the frame when `bufferedAmount`
 * exceeds the Phase 2 cap instead of queueing into a 1006 death spiral.
 */
export function processAndSendPcmFrame(
  ws: PcmWebSocket,
  samples: Float32Array,
  inRate: number,
  bufferedAmountCap = PCM_BUFFERED_AMOUNT_CAP,
  targetSampleRateHz = TARGET_SAMPLE_RATE_16K,
): ProcessPcmFrameResult {
  if (ws.readyState !== WebSocket.OPEN) return { status: "skipped", reason: "closed" };
  if (!samples?.length) return { status: "skipped", reason: "empty" };
  if (ws.bufferedAmount > bufferedAmountCap) {
    return { status: "dropped", reason: "backpressure" };
  }
  const pcm = resampleToTargetRate(samples, inRate, targetSampleRateHz);
  const int16 = float32ToInt16(pcm);
  try {
    ws.send(int16.buffer);
    return { status: "sent", bytesSent: int16.byteLength };
  } catch {
    return { status: "skipped", reason: "error" };
  }
}
